import React, { useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import {
  Button,
  constructSizes,
  Image,
  LoadingIndicator,
  Modal,
} from "@lululemon/ui-library";

import EmailBannerForm from "../banner-form/email-banner-form";
import Agreements from "../agreements/agreements";
import { TABLET_GRID_BREAKPOINT } from "../../../lib/breakpoints";

import styles from "./modal.module.scss";

const STAY_IN_TOUCH_LANDSCAPE_IMAGE_MAX_WIDTH = 400;
const STAY_IN_TOUCH_PORTRAIT_IMAGE_MAX_WIDTH = 700;

const imageSizes = constructSizes([
  {
    breakpoint: TABLET_GRID_BREAKPOINT,
    slotSize: `${STAY_IN_TOUCH_LANDSCAPE_IMAGE_MAX_WIDTH}px`,
  },
  { slotSize: `${STAY_IN_TOUCH_PORTRAIT_IMAGE_MAX_WIDTH}px` },
]);

const EmailBannerModal = ({
  isEmailBannerModalOpen,
  setEmailBannerModalOpen,
  onSubscriptionSuccess,
  onSubscriptionFailure,
  bannerProps,
  classes,
}) => {
  const {
    modalLandscapeImageUrl,
    modalPortraitImageUrl,
    modalTitle,
    modalBody,
    modalFooter,
    success: {
      title: successTitleText,
      body: successBodyText,
      button: continueShoppingText,
      footer: successFooterText,
    },
  } = bannerProps;

  const [isSubscriptionSuccess, setIsSubscriptionSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOnSubmitStart = () => setIsSubmitting(true);

  const handleOnSuccess = () => {
    setIsSubscriptionSuccess(true);
    setIsSubmitting(false);
    onSubscriptionSuccess?.();
  };

  const handleOnFailure = () => {
    setIsSubscriptionSuccess(false);
    setIsSubmitting(false);
    onSubscriptionFailure?.();
  };

  // TODO: Update to new pattern library component imports and remove attribute data-testId="loading-container"
  return (
    <section data-testid="emailBannerModal">
      <Modal
        aria-labelledby="emailModalId"
        messages={{ close: "Close" }}
        visible={isEmailBannerModalOpen}
        onRequestClose={() => setEmailBannerModalOpen(false)}
        classes={{
          ...classes,
          modal: classNames(styles.modal, classes?.modal),
          modalContent: classNames(styles.modalContent, classes?.modalContent),
          container: classNames(styles.container, classes?.container),
        }}
      >
        <div
          className={classNames(
            styles.modalWrapper,
            classes?.modalWrapper,
            isSubscriptionSuccess ? classes?.success : null,
            isSubmitting ? classes?.loading : null,
            {
              [styles.success]: isSubscriptionSuccess,
              [styles.loading]: isSubmitting,
            }
          )}
        >
          <div
            className={classNames(
              styles.imageContainer,
              classes?.imageContainer
            )}
          >
            {modalLandscapeImageUrl && (
              <Image
                maxWidth={STAY_IN_TOUCH_LANDSCAPE_IMAGE_MAX_WIDTH}
                className={classNames(
                  styles.stayInTouchImage,
                  styles.landscapeImage
                )}
                prefix="emailModal"
                lazy
                alt=""
                src={modalLandscapeImageUrl}
                sizes={imageSizes}
              />
            )}
            {modalPortraitImageUrl && (
              <Image
                maxWidth={STAY_IN_TOUCH_PORTRAIT_IMAGE_MAX_WIDTH}
                className={classNames(
                  styles.stayInTouchImage,
                  styles.portraitImage
                )}
                prefix="emailModal"
                lazy
                alt=""
                src={modalPortraitImageUrl}
                sizes={imageSizes}
              />
            )}
          </div>
          <div
            className={classNames(
              styles.modalContentWrapper,
              classes?.modalContentWrapper
            )}
          >
            {isSubmitting && (
              <div
                data-testid="loading-container"
                className={classNames(
                  styles.modalSpinnerBox,
                  classes?.modalSpinnerBox
                )}
              >
                <LoadingIndicator color="red" />
              </div>
            )}
            <div>
              <header
                className={classNames(
                  styles.modalContentHeader,
                  classes?.modalContentHeader
                )}
              >
                <h1
                  id="emailModalId"
                  className={classNames(
                    "lll-text-small",
                    styles.modalTitle,
                    classes?.modalTitle
                  )}
                >
                  {isSubscriptionSuccess ? successTitleText : modalTitle}
                </h1>
                <p
                  className={classNames(
                    "lll-text-body-2",
                    styles.modalBody,
                    styles.marginZero,
                    classes?.modalBody
                  )}
                >
                  {isSubscriptionSuccess ? successBodyText : modalBody}
                </p>
              </header>
              {isSubscriptionSuccess ? (
                <Button
                  block
                  variant="filledImpact"
                  onClick={() => setEmailBannerModalOpen(false)}
                  className={styles.continueShopping}
                >
                  {continueShoppingText}
                </Button>
              ) : (
                <EmailBannerForm
                  bannerProps={bannerProps}
                  onSubmitStart={handleOnSubmitStart}
                  onSubscriptionSuccess={handleOnSuccess}
                  onSubscriptionFailure={handleOnFailure}
                  classes={classes}
                />
              )}
            </div>
            <footer
              className={classNames(
                styles.privacyPolicyAndTermsOfUse,
                classes?.privacyPolicyAndTermsOfUse
              )}
            >
              <Agreements
                agreements={
                  isSubscriptionSuccess
                    ? successFooterText || modalFooter
                    : modalFooter
                }
              />
            </footer>
          </div>
        </div>
      </Modal>
    </section>
  );
};

EmailBannerModal.defaultProps = {
  isEmailBannerModalOpen: false,
};

EmailBannerModal.propTypes = {
  isEmailBannerModalOpen: PropTypes.bool,
  setEmailBannerModalOpen: PropTypes.func.isRequired,
  bannerProps: PropTypes.shape({
    modalLandscapeImageUrl: PropTypes.string.isRequired,
    modalPortraitImageUrl: PropTypes.string.isRequired,
    modalTitle: PropTypes.string.isRequired,
    modalBody: PropTypes.string.isRequired,
    modalFooter: PropTypes.string.isRequired,
    success: PropTypes.shape({
      title: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
      button: PropTypes.string.isRequired,
    }),
  }).isRequired,
};

export { EmailBannerModal };
